// uploadQueueHooks.js - Simplified React hooks for upload queue
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addFiles,
  startUpload,
  removeFile,
  updateComment,
  updateConfig,
  updateFileData,
  reset,
  cancelUpload,
  cancelCurrentUpload,
  UPLOAD_STATES,
  FILE_STATUS,
} from ".";
import { calculateOverallProgress, getUploadStats } from "./utils";

export const useUploadQueue = () => {
  const dispatch = useDispatch();
  const uploadQueue = useSelector((state) => state.uploadQueue);

  // Actions
  const handleAddFiles = useCallback(
    (fileList) => {
      return dispatch(addFiles(fileList));
    },
    [dispatch]
  );

  const handleStartUpload = useCallback(() => {
    return dispatch(startUpload());
  }, [dispatch]);

  const handleRemoveFile = useCallback(
    (file) => {
      return dispatch(removeFile(file));
    },
    [dispatch]
  );

  const handleUpdateComment = useCallback(
    (comment) => {
      return dispatch(updateComment(comment));
    },
    [dispatch]
  );

  const handleUpdateConfig = useCallback(
    (config) => {
      return dispatch(updateConfig(config));
    },
    [dispatch]
  );

  const handleUpdateFileData = useCallback(
    (fileData) => {
      return dispatch(updateFileData(fileData));
    },
    [dispatch]
  );

  const handleReset = useCallback(() => {
    return dispatch(reset());
  }, [dispatch]);

  const handleCancelUpload = useCallback(
    (caseId, clipId) => {
      return dispatch(cancelUpload({ caseId, clipId }));
    },
    [dispatch]
  );

  const handleCancelCurrentUpload = useCallback(() => {
    return dispatch(cancelCurrentUpload());
  }, [dispatch]);

  // Computed values
  const overallProgress = useMemo(
    () => calculateOverallProgress(uploadQueue.progress),
    [uploadQueue.progress]
  );

  const uploadStats = useMemo(
    () => getUploadStats(uploadQueue.files, uploadQueue.progress),
    [uploadQueue.files, uploadQueue.progress]
  );

  // State helpers
  const isIdle = uploadQueue.uploadState === UPLOAD_STATES.IDLE;
  const isUploading = uploadQueue.uploadState === UPLOAD_STATES.UPLOADING;
  const isComplete = uploadQueue.uploadState === UPLOAD_STATES.COMPLETE;
  const hasError = uploadQueue.uploadState === UPLOAD_STATES.ERROR;

  return {
    files: uploadQueue.files,
    fileData: uploadQueue.fileData,
    comment: uploadQueue.comment,
    config: uploadQueue.config,
    uploadState: uploadQueue.uploadState,
    progress: uploadQueue.progress,
    caseId: uploadQueue.caseId,
    clipId: uploadQueue.clipId,
    description: uploadQueue.description,

    overallProgress,
    uploadStats,

    isValid: uploadQueue.isValid,
    isIdle,
    isUploading,
    isComplete,
    uploadFinishedAt: uploadQueue.uploadFinishedAt,
    hasError,
    hasFiles: uploadQueue.files.length > 0,
    canStartUpload:
      uploadQueue.isValid &&
      uploadQueue.files.length > 0 &&
      !uploadQueue.isUploading,

    // Actions
    addFiles: handleAddFiles,
    startUpload: handleStartUpload,
    removeFile: handleRemoveFile,
    updateComment: handleUpdateComment,
    updateConfig: handleUpdateConfig,
    updateFileData: handleUpdateFileData,
    reset: handleReset,
    cancelUpload: handleCancelUpload,
    cancelCurrentUpload: handleCancelCurrentUpload,
  };
};

// individual file status hook, not currently used
// but is probably coming shortly
export const useFileStatus = (fileId) => {
  const { files, progress } = useSelector((state) => state.uploadQueue);

  const file = useMemo(
    () => files.find((f) => f.id === fileId),
    [files, fileId]
  );

  const fileProgress = useMemo(
    () =>
      progress[fileId] || {
        status: FILE_STATUS.WAITING,
        percent: 0,
        message: "",
        error: null,
      },
    [progress, fileId]
  );

  return {
    file,
    progress: fileProgress,
    exists: !!file,
    isWaiting: fileProgress.status === FILE_STATUS.WAITING,
    isHashing: fileProgress.status === FILE_STATUS.HASHING,
    isUploading: fileProgress.status === FILE_STATUS.UPLOADING,
    isComplete: fileProgress.status === FILE_STATUS.COMPLETE,
    hasError: fileProgress.status === FILE_STATUS.ERROR,
    percent: fileProgress.percent,
    message: fileProgress.message,
    error: fileProgress.error,
    currentChunk: fileProgress.currentChunk,
    totalChunks: fileProgress.totalChunks,
  };
};
